import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { AlbumData } from "@/pages/Gallery/components/Album/index";
import styles from "./index.module.scss";
import config from "@/config";
import { postListAlbums, postMoveAlbum } from "@/modules/album/api";
import { useInViewport } from "ahooks";
import OperationMenu from "@/modules/album/components/OperationMenu";
import { CreateAlbumModal } from "@/modules/album/components/CreateModal";
import { Image, useBoolean } from "@chakra-ui/react";
import { OpMenuItem } from "@/modules/album/type";
import { useTranslate } from "@/utils/lang";
import { AlbumWords } from "@/utils/lang/album";
import { useAlbumListMenuScroll } from "@/modules/album/hook/useAlbumListMenuScroll";
import { PageLoadManager } from "@/utils/tool";

export interface AlbumsItemType {
  id: number;
  name: string;
  image_count: number;
  preview_url: string[];
  is_default: 1 | 2; //是否默认画册 1是2否
}
interface AlbumsDataType {
  page_index: number;
  page_size: number;
  total: number;
  albums: AlbumsItemType[];
}
interface JoinedAlbumProps {
  isGallery?: boolean;
  albumData: AlbumData;
  onRemoveAlbum: () => void;
  onMoveAlbum: (toAlbumData: AlbumsItemType) => Promise<void>;
}
const JoinedAlbum: FC<JoinedAlbumProps> = (props) => {
  const { albumData, onRemoveAlbum, onMoveAlbum, isGallery = false } = props;
  const groupWrapperRef = useRef<HTMLDivElement>(null);
  const [groupWrapperInViewport] = useInViewport(groupWrapperRef, {});
  const [dropDown, setDropDown] = useState(false);
  const albumsManager = useMemo(() => {
    return new PageLoadManager(getListAlbums, 20);
  }, []);
  const [albumsData, setAlbumsData] = useState<AlbumsDataType["albums"]>([]);
  const [create, createOp] = useBoolean(false);

  const { translate } = useTranslate();
  useEffect(() => {
    if (!groupWrapperInViewport) {
      setDropDown(false);
    }
  }, [groupWrapperInViewport]);

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async (increase = false) => {
    const handler = increase ? albumsManager.next : albumsManager.fetch;
    const albumList = await handler();
    setAlbumsData(albumList);
  };

  const refreshAlbums = async () => {
    const albumList = await albumsManager.refreshAll();
    setAlbumsData(albumList);
  };

  async function getListAlbums(pageIdx: number, pageSize: number) {
    try {
      const res = await postListAlbums({
        page_index: pageIdx,
        page_size: pageSize,
      });
      if (res.code === 0) {
        return {
          total: res.data.total,
          data: res.data.albums,
        };
      }
      return {
        total: 0,
        data: [],
      };
    } catch (e) {
      console.error(e);
      return {
        total: 0,
        data: [],
      };
    }
  }

  const { scrollContainerRef } = useAlbumListMenuScroll({
    hasMore: albumsManager.hasMore,
    loadMore: () => fetchAlbums(true),
  });

  const handelAlbumSelect = async (albumId: string) => {
    try {
      if (albumId === "create") {
        createOp.on();
      } else {
        const album = albumsData.find((album) => album.id === Number(albumId));
        if (album) {
          await onMoveAlbum(album);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };
  const albumList: OpMenuItem[] = useMemo(() => {
    return albumsData.map((item: AlbumsItemType) => {
      return {
        key: item.id + "",
        content: item.name,
      };
    });
  }, [albumsData]);

  const menuOpen = () => {
    if (!isGallery) return;
    const card = document.querySelector(`[data-imageid='${albumData.image_id}']`);
    card?.classList.add("hover");
  };

  const menuClose = () => {
    if (!isGallery) return;
    const card = document.querySelector(`[data-imageid="${albumData.image_id}"]`);
    card?.classList.remove("hover");
  };

  return (
    <div className={styles.groupWrapper} ref={groupWrapperRef}>
      <div
        style={{ flexGrow: 1 }}
        onClick={async (e) => {
          e.stopPropagation();
          await refreshAlbums();
          setDropDown(!dropDown);
        }}
      >
        <span>
          {isGallery ? translate(AlbumWords.alreadyIn) : translate(AlbumWords.alreadyInAlbum)}
        </span>
        <OperationMenu
          onClose={menuClose}
          onOpen={menuOpen}
          value={albumData.album_id + ""}
          styles={{
            listRoot: { zIndex: 10 },
            menuList: {
              minW: "100%",
              maxH: "136px",
              bg: "#424242",
              border: "1px solid",
              borderColor: "#585858",
            },
            menuItem: {
              bg: "#424242",
            },
            divider: {
              borderColor: "#585858",
            },
          }}
          variant="creatorMoveImage"
          menuList={albumList}
          menuListRef={scrollContainerRef as any}
          staticList={[{ key: "create", content: `+ ${translate(AlbumWords.newAlbum)}` }]}
          onSelect={handelAlbumSelect}
        >
          <span>{albumData?.album_name}</span>
          <Image
            w="1em"
            display="inline-block"
            verticalAlign="middle"
            src={`${config.buildCdnBaseUrl}assets/creator/down.png`}
            alt={albumData?.album_name}
          />
        </OperationMenu>
      </div>
      <button
        className={styles.removeAlbum}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onRemoveAlbum?.();
        }}
      >
        {translate(AlbumWords.removeFromAlbum)}
      </button>
      <CreateAlbumModal isOpen={create} onClose={createOp.off} onCreate={refreshAlbums} />
    </div>
  );
};
export default observer(JoinedAlbum);
